/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useAncestorPath } from '@squiz/dxp-console-support';
import { ALERT_TYPE_WARNING, Alert, AlertProps, Breadcrumbs, BreadcrumbsProps } from '@squiz/sds';
import { DxpScreen } from 'apps/console-core/src/Components/DxpScreen/DxpScreen';
import { useCapabilityBreadcrumbs } from 'apps/console-core/src/Hooks/UseCapabilityBreadcrumbs/UseCapabilityBreadcrumbs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { JsonObject } from 'type-fest';
import { COMPONENT_SERVICE_STRINGS } from '../../Constants/Strings.constants';
import { ComponentSetDetailsResult, getComponentService } from '../../Services/component';
import { isSetNamespaceRestricted } from '../../Utilities/IsSetNamespaceRestricted/IsSetNamespaceRestricted';
import { ComponentSetDetailsComponents } from './elements/ComponentSetDetailsComponents/ComponentSetDetailsComponents';
import { ComponentSetDetailsComponentsProps } from './elements/ComponentSetDetailsComponents/ComponentSetDetailsComponents.props';
import { ComponentSetDetailsConfigs } from './elements/ComponentSetDetailsConfigs/ComponentSetDetailsConfigs';
import { ComponentSetDetailsConfigsProps } from './elements/ComponentSetDetailsConfigs/ComponentSetDetailsConfigs.props';
import { ComponentSetDetailsDescription } from './elements/ComponentSetDetailsDescription/ComponentSetDetailsDescription';

/**
 * The component set details screen.
 */
export const ComponentSetDetails = (): JSX.Element => {
  const { t } = useTranslation();
  const params = useParams();
  const webpath = params.setWebpath || '';
  const isSquizBaseComponent = isSetNamespaceRestricted(webpath);

  // Labels
  const labels = useMemo(() => {
    return {
      allComponents: t(COMPONENT_SERVICE_STRINGS.allComponents),
      componentSets: t(COMPONENT_SERVICE_STRINGS.componentSets),
      squizEditWarning: t(COMPONENT_SERVICE_STRINGS.squizEditWarning),
    };
  }, [t]);

  const [componentSetDetails, setComponentSetDetails] = useState<ComponentSetDetailsResult>([null, null]);

  // Component service
  const componentService = getComponentService();

  const updateComponentSetDetails = useCallback((): void => {
    lastValueFrom(componentService.getComponentSetDetails({ webpath }))
      .then((res) => {
        setComponentSetDetails([res, res as unknown as JsonObject]);
        return true;
      })
      .catch((err) => {
        setComponentSetDetails([null, null]);
      });
  }, [componentService, webpath]);

  useEffect(() => {
    updateComponentSetDetails && updateComponentSetDetails();
  }, [updateComponentSetDetails]);

  // Render the screen content
  let screenHeading = '';
  let screenContent = <></>;
  let breadcrumbs: JSX.Element | undefined;

  // Prepare breadcrumbs
  const capabilityBreadcrumbs = useCapabilityBreadcrumbs();
  const breadcrumbProps: BreadcrumbsProps = {
    links: [
      ...capabilityBreadcrumbs,
      {
        name: labels.componentSets,
        url: useAncestorPath(true, 2),
      },
    ],
  };

  if (componentSetDetails[0]) {
    const { displayName } = componentSetDetails[0];

    // Breadcrumbs
    breadcrumbProps.currentPageName = displayName;
    breadcrumbs = <Breadcrumbs {...breadcrumbProps} data-id="component-set-details-breadcrumbs" />;

    screenHeading = displayName;

    const alertProps: AlertProps = {
      message: labels.squizEditWarning,
      type: ALERT_TYPE_WARNING,
    };

    const configProps: ComponentSetDetailsConfigsProps = {
      componentSetProps: componentSetDetails[0],
      disabled: isSquizBaseComponent,
      updateComponentSetDetails,
    };

    const componentProps: ComponentSetDetailsComponentsProps = {
      ...componentSetDetails[0],
      disabled: isSquizBaseComponent,
      updateComponentSetDetails,
    };

    screenContent = (
      <>
        {isSquizBaseComponent && <Alert {...alertProps} classes="component-set-details-alert" />}
        <ComponentSetDetailsDescription {...componentSetDetails[0]} />
        <ComponentSetDetailsConfigs {...configProps} />
        <ComponentSetDetailsComponents {...componentProps} />
      </>
    );
  }

  return (
    <DxpScreen breadcrumbs={breadcrumbs} heading={screenHeading}>
      {screenContent}
    </DxpScreen>
  );
};
